<template>
  <div class="flex flex-col">
    <h2>Mascotas</h2>

    <div class="mt-2 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
      <custom-search
        class="w-full sm:w-[400px]"
        placeholder="Buscar por Nombre"
        v-model="searchText"
        @confirm="search"
        @clear="search"
      />

      <div class="flex flex-col">
        <label class="text-xs text-content-400 mb-1">Estado de cotización</label>
        <custom-select
          class="w-56"
          :options="quoteStatusOptions"
          v-model="quoteStatus"
          @change="applyFilter"
        />
      </div>
    </div>

    <div class="card mt-6">
      <table class="table-auto text-sm mt-2 bg-white">
        <thead class="text-left">
          <tr class="bg-primary-100">
            <th class="px-4 py-3 rounded-l-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">ID</th>
            <th
              class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide cursor-pointer select-none"
              @click="toggleSort('name')"
            >
              Nombre <span class="ml-1">{{ sortArrow('name') }}</span>
            </th>
            <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Cliente</th>
            <th
              class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide cursor-pointer select-none"
              @click="toggleSort('created_at')"
            >
              Fecha de registro <span class="ml-1">{{ sortArrow('created_at') }}</span>
            </th>
            <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Cotización</th>
            <th class="px-4 py-3 rounded-r-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="pet in pets" :key="pet.id" class="table-row">
            <td class="table-cell">
              {{ pet.id }}
            </td>
            <td class="table-cell">
              {{ pet.name }}
            </td>
            <td class="table-cell">
              <div>
                <router-link
                  v-if="pet.client"
                  :to="{
                    name: 'clientsDetail',
                    params: { id: pet.client.id },
                  }"
                  class="underline system-button"
                  >{{ pet.client.name }} {{ pet.client.last_name }}</router-link
                >
                <span v-else>—</span>
              </div>
            </td>
            <td class="table-cell whitespace-nowrap">{{ formatDate(pet.created_at) }}</td>
            <td class="table-cell">
              <span :class="['badge', quoteStatusMeta(pet.quote_status).class]">
                <span class="badge-dot"></span>
                {{ quoteStatusMeta(pet.quote_status).label }}
              </span>
            </td>
            <td class="table-cell">
              <div class="flex">
                <router-link
                  :to="{
                    name: 'petsDetail',
                    params: { id: pet.id },
                  }"
                  class="secondary-button"
                  >Detalle</router-link
                >
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="loading" class="flex w-full h-full items-center">
        <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
      </div>
    </div>

    <pagination
      :current-page="currentPage"
      :total-pages="totalPages"
      @page-change="onPageChange"
    />
  </div>
</template>

<script>
import api from "@/api";
import Pagination from "@/components/ui/Pagination.vue";

const QUOTE_STATUS_META = {
  none: { class: "badge-error", label: "Sin cotización" },
  pending_request: { class: "badge-info", label: "Solicitada" },
  requested: { class: "badge-warning", label: "Por pagar" },
  pending: { class: "badge-warning", label: "Pendiente" },
  active: { class: "badge-success", label: "Activa" },
  paused: { class: "badge-info", label: "Pausada" },
  suspended: { class: "badge-error", label: "Suspendida" },
  canceled: { class: "badge-neutral", label: "Cancelada" },
};

export default {
  name: "Pets",

  components: {
    Pagination,
  },

  data() {
    return {
      pets: null,
      loading: false,
      searchText: "",
      quoteStatus: "all",
      sort: "created_at",
      direction: "desc",
      currentPage: 1,
      totalPages: 0,
      quoteStatusOptions: [
        { label: "Todas", value: "all" },
        { label: "Sin cotización", value: "none" },
        { label: "Solicitada", value: "pending_request" },
        { label: "Activa", value: "active" },
        { label: "Pausada", value: "paused" },
        { label: "Suspendida", value: "suspended" },
        { label: "Cancelada", value: "canceled" },
      ],
    };
  },

  created() {
    this.$store.commit("setSelectedTab", "pets");

    this.loadPets();
  },

  methods: {
    async loadPets() {
      this.loading = true;

      try {
        const response = await api.pets.getAllWithQueryParams([
          { name: "page", value: this.currentPage },
          { name: "search", value: this.searchText },
          { name: "quote_status", value: this.quoteStatus },
          { name: "sort", value: this.sort },
          { name: "direction", value: this.direction },
        ]);
        this.pets = response.data;
        this.currentPage = response.current_page;
        this.totalPages = response.last_page;
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    quoteStatusMeta(status) {
      return QUOTE_STATUS_META[status] || { class: "badge-neutral", label: status || "—" };
    },

    search() {
      this.currentPage = 1;
      this.loadPets();
    },

    applyFilter() {
      this.currentPage = 1;
      this.loadPets();
    },

    toggleSort(column) {
      if (this.sort === column) {
        this.direction = this.direction === "asc" ? "desc" : "asc";
      } else {
        this.sort = column;
        this.direction = column === "created_at" ? "desc" : "asc";
      }
      this.currentPage = 1;
      this.loadPets();
    },

    sortArrow(column) {
      if (this.sort !== column) return "";
      return this.direction === "asc" ? "▲" : "▼";
    },

    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleDateString("es-MX", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadPets();
    },
  },
};
</script>
